import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Card, CardBody, Badge, Button, Input } from '@img/ui';
import { api, type ApiSuccess } from '../../lib/api';

interface PendingNameChange {
  id: string;
  name: string;
  pendingName: string;
  email: string | null;
  phone: string | null;
  pendingNameAt: string | null;
  role: string;
}

export function AdminProfileChangesPage() {
  const qc = useQueryClient();
  const [openReject, setOpenReject] = useState<{ id: string; name: string; pendingName: string } | null>(null);
  const [reason, setReason] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['admin', 'profile-changes', 'pending-names'],
    queryFn: () => api.get<ApiSuccess<PendingNameChange[]>>('/api/admin/profile-changes/pending-name-changes'),
  });

  const approveM = useMutation({
    mutationFn: (id: string) => api.post(`/api/admin/profile-changes/${id}/approve-name`, {}),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['admin', 'profile-changes'] }),
  });

  const rejectM = useMutation({
    mutationFn: ({ id, reason }: { id: string; reason: string }) =>
      api.post(`/api/admin/profile-changes/${id}/reject-name`, { reason }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'profile-changes'] });
      setOpenReject(null);
      setReason('');
    },
  });

  const list = data?.data ?? [];

  return (
    <div>
      <h1 className="text-3xl font-black text-navy-dark mb-2">📝 طلبات تغيير الاسم</h1>
      <p className="text-sm text-gray-500 mb-6">
        طلبات تعديل الاسم تتطلّب موافقة الإدارة قبل التطبيق على حسابات المستخدمين.
      </p>

      {isLoading ? (
        <div className="text-gray-500">⟳ جاري التحميل…</div>
      ) : list.length === 0 ? (
        <Card><CardBody>
          <div className="text-center py-12 text-gray-500">
            <div className="text-5xl mb-3">✓</div>
            <div>لا توجد طلبات معلّقة حالياً</div>
          </div>
        </CardBody></Card>
      ) : (
        <div className="space-y-3">
          {list.map((u) => (
            <Card key={u.id}>
              <CardBody>
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-[260px]">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <Badge color={u.role.includes('ADMIN') ? 'gold' : 'navy'}>{u.role}</Badge>
                      <span className="text-xs text-gray-500">
                        {u.pendingNameAt && new Date(u.pendingNameAt).toLocaleString('ar-SA')}
                      </span>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mb-2">
                      <div className="p-3 rounded-xl bg-gray-50 border border-gray-200">
                        <div className="text-xs text-gray-500 mb-0.5">الاسم الحالي</div>
                        <div className="font-bold text-navy-dark">{u.name}</div>
                      </div>
                      <div className="p-3 rounded-xl bg-amber-50 border border-amber-200">
                        <div className="text-xs text-amber-700 mb-0.5">الاسم المطلوب</div>
                        <div className="font-bold text-amber-900">{u.pendingName}</div>
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 space-y-0.5">
                      {u.email && <div dir="ltr">📧 {u.email}</div>}
                      {u.phone && <div dir="ltr">📱 {u.phone}</div>}
                    </div>
                  </div>

                  <div className="flex flex-col gap-2 shrink-0">
                    <Button
                      variant="primary"
                      onClick={() => approveM.mutate(u.id)}
                      loading={approveM.isPending && approveM.variables === u.id}
                    >
                      ✓ موافقة
                    </Button>
                    <Button
                      variant="ghost"
                      onClick={() => setOpenReject({ id: u.id, name: u.name, pendingName: u.pendingName })}
                    >
                      ✕ رفض
                    </Button>
                  </div>
                </div>
              </CardBody>
            </Card>
          ))}
        </div>
      )}

      {/* مودال الرفض */}
      {openReject && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setOpenReject(null)}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-xl font-black text-navy-dark mb-2">رفض طلب تغيير الاسم</h3>
            <p className="text-sm text-gray-600 mb-4">
              المستخدم: <b>{openReject.name}</b>
              <br />
              الاسم المطلوب: <b className="text-amber-700">{openReject.pendingName}</b>
            </p>
            <Input
              label="سبب الرفض (اختياري)"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="مثلاً: الاسم غير مطابق للهوية"
            />
            <div className="flex justify-end gap-2 mt-4">
              <Button variant="ghost" onClick={() => setOpenReject(null)}>إلغاء</Button>
              <Button
                variant="primary"
                onClick={() => rejectM.mutate({ id: openReject.id, reason })}
                loading={rejectM.isPending}
              >
                تأكيد الرفض
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
